import { extendObservable, toJS/*, observable*/ } from 'mobx'
import Model from './model'
// import { deepGet } from './utils'

const getProperty = (obj, prop, defaultVal) => {
  if (
    obj
    && typeof obj === 'object'
    && prop in obj
  ) {
    return obj[prop]
  }
  return defaultVal
}

/*
 * The Store object.
 */
export default class Store {

  /**
   * Creates a new store instance.
   * @param {Object} opts - Some options for this store.
   */
  constructor(opts: Object) {

    /**
     * Options for this store.
     * @type {Object}
     */
    this.opts = opts

    /**
     * The collection of the Models
     * @type {Array<Model>}
     */
    this.collection = []

    /**
     * The collection of the Models
     * @type {Array<Model>}
     */
    this.filterCollection = []

    /**
     * The currently active model
     * @type {Model}
     */
    this.current = {}

    /**
     * The model class.
     * @type {Object}
     */
    this.Model = null

    this.openingId = null
    this.openingTimeout = null
    this.openingRequest = null

  }

  addCollection(collectionName: string) {
    extendObservable(this, { [collectionName]: [] })
  }

  /**
   * Creates a new model.
   * @param {Object} data - The plain data object.
   * @param {Object} opts - The plain options object.
   * @returns {Model|Object}
   */
  createModel(data: Object = {}, opts: Object | void) {

    return this.Model
      ? new this.Model(this, data, opts)
      : data

  }

  create(data: Object = {}/* , opts: Object = {} */) {

    const model = this.createModel(data)

    this.add(model)

    return model.save()
      .then((modelData) => {
        model.set(modelData)
        return model
      })
      .catch((err) => {
        this.remove(model)
        throw err
      })

  }

  isModel(suspect) {
    return this.Model && suspect instanceof this.Model
  }

  getCollection(opts = {}) {
    if (opts.collection) {
      return opts.collection
    }

    return (opts.params && opts.params.filter && (opts.params.filter !== '[]'))
      ? 'filterCollection'
      : 'collection'
  }

  add(models, opts: Object = {}) {

    const collection = this.getCollection(opts)

    if (!Array.isArray(models)) {
      models = [models]
    }

    models = models.map((model) => {

      const exists = this.getById(model.id, opts)

      if (this.Model && (!exists && !this.isModel(model))) {
        model = this.createModel(model)
      }

      if (exists) {

        if (opts.merge) {
          // eslint-disable-next-line max-len
          console.warn(`A model with the id ${model.id} already exists in the collection. Merging.`)
          exists.set(model.getJSON())
          return exists
        }
        if (opts.replace) {
          // eslint-disable-next-line max-len
          console.warn(`A model with the id ${model.id} already exists in the collection. Replacing.`)
          return model
        }

      }

      return model

    })

    if (opts.reset) {
      this[collection].splice(0, this[collection].length, ...models)
    }
    else {
      // models required to exist in both collections, even when building a filtered collection
      if (
        opts.params && opts.params.filter && (opts.params.filter !== '[]')
        && collection !== 'collection'
      ) {
        this[collection].push(...models)
      }
      this.collection.push(...models)
    }

    return models

  }

  clear(opts = {}) {
    this[this.getCollection(opts)] = []
  }

  remove(model, opts = {}) {
    return this[this.getCollection(opts)].splice(this.collection.indexOf(model), 1)
  }

  filter(filter) {
    return this.collection.filter((item) => {
      if (!item.matches) {
        return true
      }
      return item.matches(filter)
    })
  }

  where(filter, opts = {}) {

    if (!Array.isArray(filter)) {
      filter = Object.keys(filter)
        .filter((key) => filter[key] !== -1 && filter[key] !== '-1')
        .map((key) => ({
          id: key,
          type: key,
          value: getProperty(filter[key], 'value', filter[key]),
          comparator: getProperty(filter[key], 'comparator', 'in'),
          operator: 'and'
        }))
    }

    // const result = observable(this.collection.filter((item) => {
    //   return Object.keys(filter).reduce((memo, key) => {
    //     return memo && deepGet(item, key) === deepGet(filter, key)
    //   }, true)
    // }))
    return this.load(null, {
      ...opts,
      params: {
        ...(opts.params || {}),
        filter: JSON.stringify(filter)
      }
    })
      .then((response) => {
        return response
      })
    // return result
  }

  /**
   * Load the data and populate the model[s].
   * @abstract
   * @param {?(String|Number)} id - The id of the model to load. If no id is
   * passed (or `null`) then the collection of models will be loaded.
   * @param {Object} opts - An options object to send along to the transport
   * layer.
   * @param {Object} data - A plain data object to be send along to the
   * transport layer.
   */
  // eslint-disable-next-line no-unused-vars
  load(id = null, opts: Object = {}, data: Object | null = null) { }

  /**
   * Save the data from the model.
   * @abstract
   * @param {!(String|Number)} id - The id of the model to load.
   * @param {Object} opts - An options object to send along to the transport
   * layer.
   * @param {Object} data - A plain data object to be send along to the
   * transport layer.
   */
  // eslint-disable-next-line no-unused-vars
  save(id = null, opts: Object = {}, data: Object | null = null) { }

  /**
   * Delete the model.
   * @abstract
   * @param {!(String|Number)} id - The id of the model to load.
   * @param {Object} opts - An options object to send along to the transport
   * layer.
   */
  // eslint-disable-next-line no-unused-vars
  destroy(id = null, opts: Object = {}) { }

  /**
   * Transform this model to a JSON object.
   * @returns {Object}
   */
  asJSON(): Object {
    return toJS(this)
  }

  /**
   * Gets a model from the store by it's id.
   * @returns Null if no model by this id was found.
   */
  getById(id, opts = {}): Object | void {
    const isNumericId = typeof this.usesNumericId === 'boolean' ? this.usesNumericId : !Number.isNaN(id * 1)

    if (isNumericId) {
      id *= 1
    }

    return this[this.getCollection(opts)].find((item) => item && id === (isNumericId ? item.id * 1 : item.id))

  }

  contains(model: Model | void, opts: Object = {}): boolean {
    return this[this.getCollection(opts)].indexOf(model) >= 0
  }

  /**
   * If a model with the given `id` is not found in {@link Model#collection},
   * this store will try to load it from the server.
   * If it existed already or exists now that it was loaded, then this model
   * will be set as {@link Model#current}.
   * @param {!(String|Number)} id - The id of the model.
   * @param {Object} opts - A plain object of options to send along to the
   * @property {Boolean} opts.force - If true, forces
   *  the store to actually reload (fetch) the model.
   * {@link Store#load} method.
   */
  openById(id, opts: Object = {}) {

    const forceRefetch = opts.force

    // bail out if
    // 1) there is no id,
    // 2) we are already opening this item or
    // 3) the current item IS the item to be opened
    if (
      !id
      || this.openingId === id
      || (this.current && this.current.id === id)
    ) {
      return
    }

    // cancel all possibly ongoing opening actions
    this.clearOngoingOpeningActions(id !== this.openingId)

    this.unsetCurrent()

    // get the model to be opened from the local collection
    const next = this.getById(id)

    // In case the we have the model locally aready and there is no option
    // forcing us to reload the model from the server, just use the local
    // copy
    if (next && !forceRefetch) {
      this.openLocally(next)
    }
    else {
      this.loadAndOpen(id, opts)
    }

  }

  unsetCurrent() {
    // mark the current object as not being current anymore
    if (this.current) {
      this.current.isActive = false

    }
    // TODO: this should be set to null. For that to work all calls to .current
    // have to be guared by a call to .hasCurrent
    this.current = null
  }

  /**
   * Sets the current property of this store.
   * @param {Model|null} current - The new current model or null.
   */
  setCurrent(current: Model) {
    clearTimeout(this.openingTimeout)
    if (current) {
      current.isActive = true
    }
    this.current = current
  }

  toJSON() {
    return (this.collection || []).map((itm) => itm.getJSON())
  }

  /**
   * @private
   */
  clearOngoingOpeningActions(force) {

    if (force) {

      // clear any pending opening action
      if (this.openingTimeout) {
        clearTimeout(this.openingTimeout)
      }

      if (
        this.openingRequest
        && this.openingRequest.request
        && this.openingRequest.request.abort
      ) {
        // eslint-disable-next-line no-console
        console.log(`Aborting request to ${this.openingRequest.request.url}`)
        this.openingRequest.request.abort()
      }

      this.openingId = null
      this.openingTimeout = null
      this.openingRequest = null

    }

  }

  /**
   * @private
   */
  openLocally(next: Model = {}) {

    // This timeout is necessary to prevent mobx from going into an endless
    // loop. Without this, the openArticle would cause an setCurrent on
    // the article store before this function finished and thus would result
    // in another execution of this autorun.
    this.openingTimeout = setTimeout(() => {
      // next.isActive = true
      this.clearOngoingOpeningActions(true)
      this.setCurrent(next)
    }, 1)

  }

  /**
   * @private
   */
  loadAndOpen(id, opts = {}) {

    this.openingId = id

    const storeName = this.constructor.name

    this.openingRequest = this.load(id, opts)
      .then((result) => {

        if (!result) {
          const error = new Error(`Unexpected empty result for ${storeName}#load(${id})!`)
          error.id = id
          error.opts = opts
          console.error(error)
        }

        // in case the id of the opened page does not match with the one currently
        // being opened, bail out
        if (
          result
          && (!Number.isNaN(result.id * 1)
            ? result.id * 1 !== this.openingId * 1
            : result.id !== this.openingId)
        ) {
          throw new Error(`ID missmatch on opening: openingId=${
            this.openingId
          } vs. resultId=${result.id}`)
        }
        this.openingId = null
        this.openingRequest = null

        // Remove the force option and just call this method again,
        // which now will just open the local copy of the model
        delete opts.force
        this.openById(id, opts)
      })
      .catch((ex) => {
        this.openingRequest = null
        throw ex
      })

  }
}



// WEBPACK FOOTER //
// ./src/shared/store.js